import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { DogPileStatus } from '@/sections/customerManagement/types/dogPile.types';

interface DogpileConfigValue {
  startedAt: string;
  endsAt: string;
}

function resolveDogpileStatus(value: unknown): DogPileStatus {
  if (!value || typeof value !== 'object') return 'none';

  const cfg = value as Partial<DogpileConfigValue>;

  if (!cfg.endsAt) return 'none';

  const endsAt = new Date(cfg.endsAt);
  if (isNaN(endsAt.getTime())) return 'none';

  const now = new Date();
  return endsAt > now ? 'active' : 'completed';
}

/**
 * Fetches dogpile configuration for all tenants in a single bulk query.
 * Returns a Map<email, DogPileStatus> following the same pattern as
 * useCustomerEvmScores (allTenants + doNotImpersonate).
 */
export function useBulkDogpileStatus() {
  const query = useMy(
    {
      resource: 'configuration',
      query: 'evm#dogpile',
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const dataMap = useMemo(() => {
    const map = new Map<string, DogPileStatus>();

    for (const item of query.data ?? []) {
      if (!item.username) continue;

      const status = resolveDogpileStatus(item.value);
      map.set(item.username, status);
    }

    return map;
  }, [query.data]);

  return {
    ...query,
    dataMap,
  };
}
